/**
 * Akıllı Arama — Türkçe doğal dil ayrıştırıcı (yapay zeka yedeği).
 *
 * Yapay zeka pasif/hatalı olduğunda, AI ile birebir aynı `AramaKriterleri`
 * şeklini üretir. Saf fonksiyonlar — React/IO bağımlılığı yok.
 */

export interface AramaKriterleri {
  odaTipleri: string[];
  minFiyat: number | null;
  maxFiyat: number | null;
  minM2: number | null;
  maxM2: number | null;
  m2Turu: 'net' | 'brut' | null;
  yeniProje: boolean | null;
  sehir: string | null;
  projeAdi: string | null;
  katTercihi: string | null;
  cephe: string | null;
  manzara: string | null;
  islemTuru: 'Satilik' | 'Kiralik' | null;
  gayrimenkulTuru: 'Konut' | 'IsYeri' | 'Arsa' | 'Tarla' | null;
  serbestAnahtarKelimeler: string[];
}

export const bosKriterler = (): AramaKriterleri => ({
  odaTipleri: [], minFiyat: null, maxFiyat: null, minM2: null, maxM2: null, m2Turu: null,
  yeniProje: null, sehir: null, projeAdi: null, katTercihi: null, cephe: null, manzara: null,
  islemTuru: null, gayrimenkulTuru: null, serbestAnahtarKelimeler: [],
});

/* ---------- Türkçe metin yardımcıları ---------- */

/** Türkçe güvenli küçültme: İ→i, I→ı dönüşümü toLocaleLowerCase'den ÖNCE yapılır. */
export const trLower = (s: string): string =>
  (s || '').replace(/İ/g, 'i').replace(/I/g, 'ı').toLocaleLowerCase('tr-TR');

/** Küçültür + aksanları katlar + noktalama'yı boşluğa çevirir. Karşılaştırmalarda iki tarafta da kullanılmalı. */
export const trFold = (s: string): string =>
  trLower(s)
    .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
    .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    .replace(/â/g, 'a').replace(/î/g, 'i').replace(/û/g, 'u')
    .replace(/[^a-z0-9+.,-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const trIcerir = (kaynak: string | null | undefined, aranan: string | null | undefined): boolean => {
  if (!kaynak || !aranan) return false;
  const k = trFold(kaynak), a = trFold(aranan);
  return k.length > 0 && a.length > 0 && k.includes(a);
};

/** "1.250.000" → 1250000, "1,5" → 1.5, "750" → 750 */
export const parseTrSayi = (s: string): number => {
  if (!s) return NaN;
  let t = s.trim().replace(/\s/g, '');
  // Binlik ayracı: tam 3 haneli gruplar halinde noktalar
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)) t = t.replace(/\./g, '').replace(',', '.');
  else t = t.replace(',', '.');
  return parseFloat(t);
};

const CARPANLAR: Record<string, number> = { bin: 1e3, milyon: 1e6, milyar: 1e9 };

export const normalizeOdaTipi = (s: string): string | null => {
  const f = trFold(s);
  if (/^(studyo|stodyo|stdyo)$/.test(f)) return '1+0';
  const m = f.match(/(\d{1,2})\s*\+\s*(\d{1,2})/);
  return m ? `${m[1]}+${m[2]}` : null;
};

const IL_LISTESI = [
  'adana','adiyaman','afyonkarahisar','agri','amasya','ankara','antalya','artvin','aydin','balikesir',
  'bilecik','bingol','bitlis','bolu','burdur','bursa','canakkale','cankiri','corum','denizli','diyarbakir',
  'edirne','elazig','erzincan','erzurum','eskisehir','gaziantep','giresun','gumushane','hakkari','hatay',
  'isparta','mersin','istanbul','izmir','kars','kastamonu','kayseri','kirklareli','kirsehir','kocaeli',
  'konya','kutahya','malatya','manisa','kahramanmaras','mardin','mugla','mus','nevsehir','nigde','ordu',
  'rize','sakarya','samsun','siirt','sinop','sivas','tekirdag','tokat','trabzon','tunceli','sanliurfa',
  'usak','van','yozgat','zonguldak','aksaray','bayburt','karaman','kirikkale','batman','sirnak','bartin',
  'ardahan','igdir','yalova','karabuk','kilis','osmaniye','duzce',
];

const STOPWORDS = new Set([
  'ariyor','ariyorum','istiyor','istiyorum','olsun','icin','bir','var','ile','gibi','tane','adet','lutfen',
  'musteri','musterim','bakiyor','bakiyorum','arasi','arasinda','civari','civarinda','yaklasik','kadar',
  'butce','butcesi','fiyat','fiyati','tl','lira','olan','olur','lazim','gerek','tercihen','tercih','daire',
  'ev','konut','satilik','kiralik','yeni','sifir','proje','projede','projesi','arayan','uygun','en','az',
  'fazla','cok','maksimum','minimum','min','max','maks','uzeri','ustu','alti','altinda','uzerinde','ve',
  'veya','ya','de','da','bu','su','o','ise','ama','fakat','ancak','metrekare','m2',
]);

interface Parca { s: number; e: number; }
const cakisiyorMu = (parcalar: Parca[], s: number, e: number) => parcalar.some(p => s < p.e && e > p.s);

/**
 * Serbest Türkçe cümleyi yapısal kriterlere çevirir.
 */
export const parseAramaKriterleri = (metin: string): AramaKriterleri => {
  const k = bosKriterler();
  if (!metin || !metin.trim()) return k;

  const f = trFold(metin);
  const tuketilen: Parca[] = [];
  const tuket = (m: RegExpMatchArray | null) => {
    if (m && m.index !== undefined) tuketilen.push({ s: m.index, e: m.index + m[0].length });
  };

  /* --- 1) m² (fiyattan ÖNCE — birim etiketi olduğu için güvenli) --- */
  const m2Aralik = f.match(/(\d[\d.,]*)\s*(?:-|–|ile|ila)\s*(\d[\d.,]*)\s*(?:m2|m²|metrekare|metre kare)/);
  if (m2Aralik) {
    k.minM2 = parseTrSayi(m2Aralik[1]);
    k.maxM2 = parseTrSayi(m2Aralik[2]);
    tuket(m2Aralik);
  } else {
    const m2Tek = f.match(/(en az|minimum|min|en fazla|maksimum|maks|max|)\s*(\d[\d.,]*)\s*(?:m2|m²|metrekare|metre kare)/);
    if (m2Tek) {
      const deger = parseTrSayi(m2Tek[2]);
      const yon = m2Tek[1];
      if (/en fazla|maksimum|maks|max/.test(yon)) k.maxM2 = deger;
      else k.minM2 = deger; // yönsüz "120 m2" → en az kabul edilir
      tuket(m2Tek);
    }
  }
  if (/\bnet\b/.test(f)) k.m2Turu = 'net';
  else if (/\bbrut\b/.test(f)) k.m2Turu = 'brut';

  /* --- 2) Fiyat aralığı: "5-7 milyon", "500 bin - 1 milyon" --- */
  const fiyatAralik = f.match(/(\d[\d.,]*)\s*(bin|milyon|milyar)?\s*(?:-|–|—|ile|ila|arasi|arasinda)\s*(\d[\d.,]*)\s*(bin|milyon|milyar)?/);
  if (fiyatAralik && !cakisiyorMu(tuketilen, fiyatAralik.index!, fiyatAralik.index! + fiyatAralik[0].length)) {
    const c1 = fiyatAralik[2] ? CARPANLAR[fiyatAralik[2]] : null;
    const c2 = fiyatAralik[4] ? CARPANLAR[fiyatAralik[4]] : null;
    // Tek çarpan (sonda) verilmişse iki tarafa da uygulanır: "5-7 milyon"
    const carpan1 = c1 ?? c2 ?? 1;
    const carpan2 = c2 ?? c1 ?? 1;
    k.minFiyat = parseTrSayi(fiyatAralik[1]) * carpan1;
    k.maxFiyat = parseTrSayi(fiyatAralik[3]) * carpan2;
    tuket(fiyatAralik);
  }

  /* --- 3) Yönlü tek değer / bütçe --- */
  if (k.minFiyat === null && k.maxFiyat === null) {
    const MIN_RE = 'en az|minimum|min|uzeri|ustu|uzerinde|baslayan|yukarisi';
    const MAX_RE = 'en fazla|en cok|maksimum|maks|max|alti|altinda|kadar|gecmeyen|asmayan|butce|butcesi';
    const YAKLASIK_RE = 'civari|civarinda|yaklasik';

    // Önce/sonra sözcük yakalayan tek geçiş
    const re = new RegExp(
      `(?:(${MIN_RE}|${MAX_RE}|${YAKLASIK_RE})\\s+)?(\\d[\\d.,]*)\\s*(bin|milyon|milyar)?\\s*(?:tl|lira|₺)?\\s*(?:(${MIN_RE}|${MAX_RE}|${YAKLASIK_RE}))?`,
      'g'
    );
    let m: RegExpExecArray | null;
    while ((m = re.exec(f)) !== null) {
      if (!m[2]) continue;
      if (cakisiyorMu(tuketilen, m.index, m.index + m[0].length)) continue;
      const yon = (m[1] || m[4] || '').trim();
      const carpan = m[3] ? CARPANLAR[m[3]] : 1;
      let deger = parseTrSayi(m[2]) * carpan;
      if (!isFinite(deger) || deger <= 0) continue;

      const paraBaglami = !!m[3] || /tl|lira|₺|butce/.test(m[0]) || new RegExp(MAX_RE).test(yon) || new RegExp(MIN_RE).test(yon);
      if (!paraBaglami) continue;

      // Çarpansız küçük sayı + para bağlamı → milyon varsayımı ("10 milyon bütçesi" zaten çarpanlı;
      // "10 bütçesi var" gibi durumlar için)
      if (!m[3] && deger <= 100) deger = deger * 1e6;

      if (new RegExp(YAKLASIK_RE).test(yon)) { k.minFiyat = deger * 0.9; k.maxFiyat = deger * 1.1; }
      else if (new RegExp(MIN_RE).test(yon)) k.minFiyat = deger;
      else k.maxFiyat = deger; // bütçe/max/yönsüz → üst sınır
      tuketilen.push({ s: m.index, e: m.index + m[0].length });
      break;
    }
  }

  /* --- 4) Oda tipi --- */
  const odaRe = /(\d{1,2})\s*\+\s*(\d{1,2})/g;
  let om: RegExpExecArray | null;
  while ((om = odaRe.exec(f)) !== null) {
    const t = `${om[1]}+${om[2]}`;
    if (!k.odaTipleri.includes(t)) k.odaTipleri.push(t);
    tuketilen.push({ s: om.index, e: om.index + om[0].length });
  }
  if (/studyo|stodyo/.test(f) && !k.odaTipleri.includes('1+0')) k.odaTipleri.push('1+0');

  /* --- 5) Sıfır / yeni proje --- */
  if (/ikinci el|2\. el|2 el|eski bina|eski/.test(f)) k.yeniProje = false;
  else if (/sifir|yeni proje|yeni bir proje|yeni projede|projeden|temelden|insaat halinde|yapim asamasinda|\byeni\b/.test(f)) k.yeniProje = true;

  /* --- 6) İşlem türü --- */
  if (/kiralik|kirali|kiraya|kiralamak|kira/.test(f)) k.islemTuru = 'Kiralik';
  else if (/satilik|satin|almak|alacak|butce|yatirim/.test(f) || k.minFiyat !== null || k.maxFiyat !== null) k.islemTuru = 'Satilik';

  /* --- 7) Gayrimenkul türü --- */
  if (/ofis|dukkan|is yeri|isyeri|magaza|depo|plaza|ticari/.test(f)) k.gayrimenkulTuru = 'IsYeri';
  else if (/\barsa\b|imarli/.test(f)) k.gayrimenkulTuru = 'Arsa';
  else if (/tarla|bag\b|zeytinlik|bahce arazi/.test(f)) k.gayrimenkulTuru = 'Tarla';
  else if (/daire|konut|\bev\b|rezidans|villa/.test(f) || k.odaTipleri.length > 0) k.gayrimenkulTuru = 'Konut';

  /* --- 8) Cephe (yön kelimesi + cephe/bakan bağlamı) --- */
  const cepheEsleme = f.match(/(guneybati|guneydogu|kuzeybati|kuzeydogu|guney|kuzey|dogu|bati)\s*(?:cephe|cepheli|bakan|bakıyor)?/);
  if (cepheEsleme && /cephe|cepheli|bakan/.test(f)) {
    k.cephe = cepheEsleme[1];
  }

  /* --- 9) Manzara --- */
  const manzaraEsleme = f.match(/(deniz|orman|sehir|gol|bogaz|park|doga|havuz)\s*(?:manzara|manzarali|manzarasi|goren)/);
  if (manzaraEsleme) k.manzara = manzaraEsleme[1];

  /* --- 10) Kat --- */
  const katNo = f.match(/(\d{1,2})\s*\.?\s*kat/);
  if (katNo) k.katTercihi = `${katNo[1]}. kat`;
  else if (/giris kat|zemin kat|\bzemin\b/.test(f)) k.katTercihi = 'giriş';
  else if (/bahce kat|bahceli kat/.test(f)) k.katTercihi = 'bahçe';
  else if (/ara kat/.test(f)) k.katTercihi = 'ara kat';
  else if (/son kat|cati kat|teras kat/.test(f)) k.katTercihi = 'son kat';
  else if (/yuksek kat/.test(f)) k.katTercihi = 'yüksek kat';

  /* --- 11) Şehir --- */
  for (const il of IL_LISTESI) {
    if (new RegExp(`\\b${il}\\b`).test(f)) { k.sehir = il; break; }
  }

  /* --- 12) Serbest anahtar kelimeler --- */
  const kalan = f
    .split(' ')
    .filter(t => t.length >= 4 && !STOPWORDS.has(t) && !/^\d/.test(t) && !/\d\+\d/.test(t))
    .filter(t => t !== k.sehir && t !== k.cephe && t !== k.manzara);
  k.serbestAnahtarKelimeler = Array.from(new Set(kalan)).slice(0, 6);

  return k;
};

/**
 * Her iki yoldan (AI veya yerel) sonra çalışır.
 * Proje adını YEREL olarak eşler (proje adları yapay zekaya hiç gönderilmez) ve
 * AI'dan gelen oda tiplerini yeniden normalize eder.
 */
export const zenginlestirKriterler = (
  kriterler: AramaKriterleri,
  metin: string,
  projeAdlari: string[] = []
): AramaKriterleri => {
  const k: AramaKriterleri = { ...kriterler, odaTipleri: [...(kriterler.odaTipleri || [])] };

  k.odaTipleri = k.odaTipleri
    .map(t => normalizeOdaTipi(t) || t)
    .filter((t, i, arr) => /^\d{1,2}\+\d{1,2}$/.test(t) && arr.indexOf(t) === i);

  if (!k.projeAdi) {
    const bulunan = projeAdlari.find(p => p && trIcerir(metin, p));
    if (bulunan) k.projeAdi = bulunan;
  }

  return k;
};

export interface KriterEtiketi {
  alan: 'odaTipleri' | 'fiyat' | 'm2' | 'yeniProje' | 'sehir' | 'projeAdi' | 'katTercihi' | 'cephe' | 'manzara' | 'islemTuru' | 'gayrimenkulTuru';
  etiket: string;
}

const paraFmt = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const basHarfBuyuk = (s: string) => (s ? s.charAt(0).toLocaleUpperCase('tr-TR') + s.slice(1) : s);

/** UI'da gösterilecek, tek tek silinebilen kriter çipleri. */
export const kriterEtiketleri = (k: AramaKriterleri): KriterEtiketi[] => {
  const list: KriterEtiketi[] = [];

  if (k.odaTipleri.length > 0) list.push({ alan: 'odaTipleri', etiket: k.odaTipleri.join(' / ') });

  if (k.minFiyat !== null && k.maxFiyat !== null) list.push({ alan: 'fiyat', etiket: `${paraFmt(k.minFiyat)} – ${paraFmt(k.maxFiyat)} ₺` });
  else if (k.maxFiyat !== null) list.push({ alan: 'fiyat', etiket: `≤ ${paraFmt(k.maxFiyat)} ₺` });
  else if (k.minFiyat !== null) list.push({ alan: 'fiyat', etiket: `≥ ${paraFmt(k.minFiyat)} ₺` });

  const m2Etiket = k.m2Turu === 'net' ? ' net' : k.m2Turu === 'brut' ? ' brüt' : '';
  if (k.minM2 !== null && k.maxM2 !== null) list.push({ alan: 'm2', etiket: `${k.minM2}–${k.maxM2} m²${m2Etiket}` });
  else if (k.maxM2 !== null) list.push({ alan: 'm2', etiket: `≤ ${k.maxM2} m²${m2Etiket}` });
  else if (k.minM2 !== null) list.push({ alan: 'm2', etiket: `≥ ${k.minM2} m²${m2Etiket}` });

  if (k.yeniProje === true) list.push({ alan: 'yeniProje', etiket: 'Sıfır / yeni proje' });
  if (k.yeniProje === false) list.push({ alan: 'yeniProje', etiket: 'İkinci el' });
  if (k.projeAdi) list.push({ alan: 'projeAdi', etiket: k.projeAdi });
  if (k.sehir) list.push({ alan: 'sehir', etiket: basHarfBuyuk(k.sehir) });
  if (k.katTercihi) list.push({ alan: 'katTercihi', etiket: basHarfBuyuk(k.katTercihi) });
  if (k.cephe) list.push({ alan: 'cephe', etiket: `${basHarfBuyuk(k.cephe)} cephe` });
  if (k.manzara) list.push({ alan: 'manzara', etiket: `${basHarfBuyuk(k.manzara)} manzara` });
  if (k.islemTuru) list.push({ alan: 'islemTuru', etiket: k.islemTuru === 'Kiralik' ? 'Kiralık' : 'Satılık' });
  if (k.gayrimenkulTuru && k.gayrimenkulTuru !== 'Konut') list.push({ alan: 'gayrimenkulTuru', etiket: k.gayrimenkulTuru });

  return list;
};
